/**
 * @file text-color.ts
 * @description Цвет текста — значение общего поля ColorField в режиме text
 * (phase19.16, партия SB120 «свобода выбора», этап 1; site-builder.md SB120,
 * SB8; проект данных docs/phases/phase19.16-materials/db-design-sb120-stage1.md
 * §1): роль палитры темы или свой цвет. «Авто» (по фону) — это **отсутствие
 * ключа** у поля, а не вариант значения: так наследование одно на всю схему
 * секции (правило §1 «нет ключа = как у сайта / Авто»).
 *
 * Пользуются: цвет текста на фото фона секции (`look.bg.textColor`), цвет
 * шапки раздела и её линии (`header.color`, `header.line.color`). Значение
 * кабинета `ColorTextValue` = `SiteTextColor | { kind: "auto" }`, где auto ↔
 * «нет ключа».
 *
 * Роль хранится ролью, не цветом: смена темы перекрашивает сама.
 */

import { z } from "zod";

import type { SiteColorRole } from "./keys.js";
import { siteFillColorSchema } from "./surface-fill.js";

/** Роли текста (как `TEXT_COLOR_ROLES` элемента, без auto). */
export const SITE_TEXT_COLOR_ROLES = ["text", "textMuted", "accent", "onDark"] as const satisfies readonly SiteColorRole[];
export type SiteTextColorRole = (typeof SITE_TEXT_COLOR_ROLES)[number];

/** Цвет текста: роль темы или свой цвет (#rrggbb / #rrggbbaa). */
export const siteTextColorSchema = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("role"), role: z.enum(SITE_TEXT_COLOR_ROLES) }),
  z.strictObject({ kind: z.literal("custom"), color: siteFillColorSchema }),
]);
export type SiteTextColor = z.infer<typeof siteTextColorSchema>;

/**
 * Цвет текста с явным «Авто» (SB120 этап 4, db-design-sb120-stage4 §1.1):
 * `{ kind: "auto" }` — по заливке (тон поверхности), иначе те же варианты,
 * что у `SiteTextColor`. Нужен там, где у поля есть умолчание-роль
 * (`theme.cart.totalColor` — «Акцент»): «Авто» как отсутствие ключа
 * `mergeMissingKeys` заменил бы умолчанием, и выбор не сохранился бы.
 * `kind` разный — умолчание-роль со значением «Авто» не сливается.
 * Совпадает с кабинетным `ColorTextValue`; тот же тип берёт SB119
 * (`theme.header.mobileMenu`).
 */
export const siteAutoTextColorSchema = z.discriminatedUnion("kind", [
  z.strictObject({ kind: z.literal("auto") }),
  ...siteTextColorSchema.options,
]);
export type SiteAutoTextColor = z.infer<typeof siteAutoTextColorSchema>;
